import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { pacientesService } from '../api/pacientes';
import { useRut } from '../hooks/useRut';
import EditPatientModal from '../components/pacientes/EditPatientModal';
import AddButton from '../components/ui/AddButton';

// Iconos SVG inline para evitar dependencias adicionales
const SearchIcon = () => (
  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const PencilIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-4 h-4 text-gray-400 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const MailIcon = () => (
  <svg className="w-4 h-4 text-gray-400 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

// Componente Avatar con iniciales
const PatientAvatar = ({ nombre }) => {
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="w-10 h-10 rounded-full bg-[#2A9D8F] text-white flex items-center justify-center font-semibold text-sm flex-shrink-0">
      {getInitials(nombre)}
    </div>
  );
};

// Componente Empty State
const EmptyState = ({ isSearch }) => (
  <div className="flex flex-col items-center justify-center py-16">
    <UsersIcon />
    <p className="mt-4 text-gray-500 text-lg">
      {isSearch ? 'No se encontraron pacientes' : 'No hay pacientes registrados'}
    </p>
    {isSearch && (
      <p className="text-gray-400 text-sm mt-1">Intenta con otro término de búsqueda</p>
    )}
  </div>
);

const PacientesPage = () => {
  const navigate = useNavigate();
  const [pacientes, setPacientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPaciente, setSelectedPaciente] = useState(null);
  const [rutOriginal, setRutOriginal] = useState('');
  const { rut, rutError, handleRutChange, setRut, verificandoExistencia, pacienteExistente } = useRut(rutOriginal);
  
  // Estado para internacionalización del documento de identidad
  const [country, setCountry] = useState('CL'); // 'CL' | 'OT'
  const [documentoId, setDocumentoId] = useState('');
  const [documentoIdError, setDocumentoIdError] = useState('');
  
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '+56',
    correo: '',
    enfermedad_base: '',
    contacto_emergencia: '',
    caso_clinico: '',
    direccion: '',
    fecha_nacimiento: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Capitalizar nombre
  const handleNombreChange = (e) => {
    const raw = e.target.value;
    const capitalized = raw.replace(/\b\w/g, (char) => char.toUpperCase());
    setFormData({ ...formData, nombre: capitalized });
  };

  // Formatear teléfono con código de país
  const handleTelefonoChange = (e) => {
    let raw = e.target.value.replace(/[^0-9+]/g, '');
    if (raw && !raw.startsWith('+')) {
      raw = '+56' + raw.replace(/^\+?56/, '');
    }
    setFormData({ ...formData, telefono: raw });
  };
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);

  useEffect(() => {
    cargarPacientes();
  }, []);

  const mostrarNotificacion = (mensaje, tipo = 'info') => {
    let claseBase = 'fixed z-[60] p-4 rounded-lg shadow-lg max-w-md';
    let claseColor;
    
    switch (tipo) {
      case 'error':
        claseColor = 'bg-red-500 text-white';
        break;
      case 'success':
        claseColor = 'bg-[#2A9D8F] text-white';
        break;
      case 'warning':
        claseColor = 'bg-yellow-500 text-white';
        break;
      case 'info':
      default:
        claseColor = 'bg-[#2A9D8F] text-white';
        break;
    }
    
    const notification = document.createElement('div');
    notification.className = `${claseBase} ${claseColor} top-4 right-4`;
    notification.textContent = mensaje;
    
    document.body.appendChild(notification);
    
    const duracion = tipo === 'error' ? 8000 : 3000;
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, duracion);
    
    return notification;
  };

  const cargarPacientes = async () => {
    try {
      const response = await pacientesService.getAll();
      setPacientes(response.data);
    } catch (error) {
      console.error('Error al cargar pacientes:', error);
      mostrarNotificacion('Error al cargar la lista de pacientes', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Validación para documentos internacionales (CURP, DNI, Pasaporte)
  const handleDocumentoIdChange = (e) => {
    const valor = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setDocumentoId(valor);
    
    if (valor.length > 0 && valor.length < 6) {
      setDocumentoIdError('El documento debe tener al menos 6 caracteres');
    } else {
      setDocumentoIdError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validación según el país seleccionado
    if (country === 'CL') {
      if (pacienteExistente) {
        mostrarNotificacion('No se puede crear el paciente porque ya existe uno con este RUT', 'error');
        return;
      }
    } else if (country === 'OT') {
      // OT country — documento opcional
    }

    setIsSubmitting(true);

    try {
      // Solo enviar documento_id si tiene valor (para OT es opcional)
      const payload = {
        pais: country,
        ...formData
      };
      if (country === 'CL' && rut) {
        payload.documento_id = rut;
      } else if (country === 'OT' && documentoId) {
        payload.documento_id = documentoId;
      }
      
      await pacientesService.create(payload);
      
      setShowForm(false);
      await cargarPacientes();
      
      // Reset form
      setRut('');
      setDocumentoId('');
      setDocumentoIdError('');
      setCountry('CL');
      setFormData({
        nombre: '',
        telefono: '+56',
        correo: '',
        enfermedad_base: '',
        contacto_emergencia: '',
        caso_clinico: '',
        direccion: '',
        fecha_nacimiento: ''
      });
      
      mostrarNotificacion('Paciente creado exitosamente', 'success');
    } catch (error) {
      console.error('Error al crear paciente:', error);
      
      let mensajeError = 'Error al crear el paciente';
      
      if (error.response) {
        if (error.response.data && error.response.data.error) {
          mensajeError = error.response.data.error;
        } else if (error.response.data && typeof error.response.data === 'string') {
          mensajeError = error.response.data;
        }
      }
      
      mostrarNotificacion(mensajeError, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Abrir modal de edición
  const handleEditClick = (paciente) => {
    setSelectedPaciente(paciente);
    setRut(paciente.rut || '');
    setRutOriginal(paciente.rut || '');
    setDocumentoId(paciente.documento_id || '');
    setCountry(paciente.pais || 'CL');
    setShowEditModal(true);
  };

  // Cerrar modal de edición
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedPaciente(null);
    setRut('');
    setRutOriginal('');
    setDocumentoId('');
    setDocumentoIdError('');
    setCountry('CL');
  };

  // Actualizar paciente desde el modal
  const handleUpdateFromModal = async (formData) => {
    setIsUpdating(true);
    
    try {
      const documentoFinal = country === 'CL' ? rut : documentoId;
      
      const datosActualizados = { 
        ...formData, 
        documento_id: documentoFinal,
        pais: country,
      };
      
      if (datosActualizados.fecha_nacimiento === '') {
        datosActualizados.fecha_nacimiento = null;
      }
      
      await pacientesService.update(selectedPaciente.id, datosActualizados);
      
      setShowEditModal(false);
      setSelectedPaciente(null);
      await cargarPacientes();
      
      setRut('');
      setRutOriginal('');
      setDocumentoId('');
      setDocumentoIdError('');
      setCountry('CL');
      
      mostrarNotificacion('Paciente actualizado exitosamente', 'success');
    } catch (error) {
      console.error('Error al actualizar paciente:', error);
      
      let mensajeError = 'Error al actualizar el paciente';
      
      if (error.response) {
        if (error.response.data && error.response.data.error) {
          mensajeError = error.response.data.error;
        } else if (error.response.data && typeof error.response.data === 'string') {
          mensajeError = error.response.data;
        }
      }
      
      mostrarNotificacion(mensajeError, 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteClick = async (id) => {
    if (window.confirm('¿Está seguro de que desea eliminar este paciente? Esta acción no se puede deshacer.')) {
      setIsDeleting(id);
      
      try {
        await pacientesService.delete(id);
        await cargarPacientes();
        mostrarNotificacion('Paciente eliminado exitosamente', 'success');
      } catch (error) {
        console.error('Error al eliminar paciente:', error);
        
        let mensajeError = 'Error al eliminar el paciente';
        
        if (error.response) {
          if (error.response.data && error.response.data.error) {
            mensajeError = error.response.data.error;
          } else if (error.response.data && typeof error.response.data === 'string') {
            mensajeError = error.response.data;
          }
        }
        
        mostrarNotificacion(mensajeError, 'error');
      } finally {
        setIsDeleting(null);
      }
    }
  };

  const limpiarRut = (rut) => {
    return (rut || '').replace(/[^0-9kK]/g, '');
  };

  const filteredPacientes = pacientes.filter(paciente => {
    const searchTermLower = searchTerm.toLowerCase().trim();
    
    if (!searchTermLower) return true;
    
    const nombreMatch = paciente.nombre.toLowerCase().includes(searchTermLower);
    
    let documentoMatch = false;
    if (paciente.documento_id) {
      documentoMatch = paciente.documento_id.toLowerCase().includes(searchTermLower);
    }
    
    let rutMatch = false;
    if (/[0-9kK]/.test(searchTerm)) {
      const searchTermLimpio = limpiarRut(searchTerm);
      const rutPacienteLimpio = limpiarRut(paciente.rut);
      rutMatch = rutPacienteLimpio.includes(searchTermLimpio);
    }
    
    // Buscar también por país
    const paisMatch = paciente.pais && paciente.pais.toLowerCase().includes(searchTermLower);
    
    return nombreMatch || rutMatch || documentoMatch || paisMatch;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2A9D8F]"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* ═══════════════════════════════════════════════════════════════════
          CONTENEDOR PRINCIPAL - Surface Card
      ═══════════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        
        {/* ─────────────────────────────────────────────────────────────────
            HEADER: Título + Botón Nuevo
        ───────────────────────────────────────────────────────────────── */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
            <h1 className="text-2xl font-bold text-[#0D2B28]">Pacientes</h1>
            {showForm ? (
              <button
                onClick={() => {
                  setRut('');
                  setRutOriginal('');
                  setDocumentoId('');
                  setDocumentoIdError('');
                  setCountry('CL');
                  setFormData({
                    nombre: '',
                    telefono: '+56',
                    correo: '',
                    enfermedad_base: '',
                    contacto_emergencia: '',
                    caso_clinico: '',
                    direccion: '',
                    fecha_nacimiento: ''
                  });
                  setShowForm(false);
                }}
                className="px-5 py-2.5 rounded-lg font-medium shadow-md transition-all duration-200 active:scale-95 bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                Cancelar
              </button>
            ) : (
              <AddButton
                onClick={() => setShowForm(true)}
              >
                Nuevo Paciente
              </AddButton>
            )}
          </div>
        </div>

        {/* ─────────────────────────────────────────────────────────────────
            BUSCADOR CÁPSULA
        ───────────────────────────────────────────────────────────────── */}
        <div className="p-6 pb-4">
          <div className="relative max-w-md">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder="Buscar por nombre, RUT o documento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 text-base border border-gray-200 rounded-full focus:outline-none focus:border-[#2A9D8F] focus:ring-2 focus:ring-[#2A9D8F]/20 transition-all duration-200"
            />
          </div>
        </div>

        {/* ─────────────────────────────────────────────────────────────────
            FORMULARIO NUEVO PACIENTE (Inline)
        ───────────────────────────────────────────────────────────────── */}
        {showForm && (
          <div className="px-6 pb-6">
            <form onSubmit={handleSubmit} className="bg-gray-50 p-6 rounded-xl">
              <h2 className="text-lg font-semibold text-[#0D2B28] mb-4">
                Nuevo Paciente
              </h2>
              {/* Selector de País */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">País de Residencia</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      name="country" 
                      value="CL" 
                      checked={country === 'CL'} 
                      onChange={() => setCountry('CL')}
                      className="text-[#2a9d8f] focus:ring-[#2a9d8f]" 
                    />
                    <span className="text-sm">Chile</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      name="country" 
                      value="OT" 
                      checked={country === 'OT'} 
                      onChange={() => setCountry('OT')}
                      className="text-[#2a9d8f] focus:ring-[#2a9d8f]" 
                    />
                    <span className="text-sm">Otro</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Campo de identificación condicional según el país */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                    {country === 'CL' ? 'RUT (opcional)' : 'Documento de Identidad (opcional)'}
                  </label>
                  <div className="relative">
                    {country === 'CL' ? (
                      // Campo RUT para Chile (opcional)
                      <>
                        <input
                          type="text"
                          value={rut}
                          onChange={handleRutChange}
                          className={`w-full px-4 py-2.5 rounded-lg border shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] transition-all ${
                            rutError 
                              ? 'border-red-300' 
                              : pacienteExistente
                                ? 'border-red-300'
                                : 'border-gray-300'
                          }`}
                          placeholder="12.345.678-9 (opcional)"
                        />
                        {verificandoExistencia && (
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#2A9D8F]"></div>
                          </div>
                        )}
                      </>
                    ) : (
                      // Campo documento opcional para Otro país
                      <input
                        type="text"
                        value={documentoId}
                        onChange={handleDocumentoIdChange}
                        className="w-full px-4 py-2.5 rounded-lg border border-gray-300 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] transition-all"
                        placeholder="Pasaporte o ID (opcional)"
                        maxLength={20}
                      />
                    )}
                  </div>
                  {/* Mensajes de error según el tipo de documento */}
                  {country === 'CL' && rutError && <p className="mt-1 text-sm text-red-600">{rutError}</p>}
                  {country === 'CL' && pacienteExistente && !rutError && (
                    <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
                      <p className="font-medium text-yellow-800">Paciente ya registrado</p>
                      <p className="text-yellow-700 mt-1">Nombre: {pacienteExistente.nombre}</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Nombre <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={handleNombreChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] transition-all"
                    required
                    placeholder="Ej: María García"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Teléfono <span className="text-red-500">*</span></label>
                  <input
                    type="tel"
                    value={formData.telefono}
                    onChange={handleTelefonoChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] transition-all"
                    required
                    placeholder="+56912345678"
                  />
                  <p className="text-xs text-gray-400 mt-1">Para Chile: +56 9 XXXX XXXX</p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Correo</label>
                  <input
                    type="email"
                    value={formData.correo}
                    onChange={(e) => setFormData({ ...formData, correo: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] transition-all"
                    placeholder="paciente@correo.cl"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Enfermedad Base</label>
                  <div className="relative">
                    <select
                      value={formData.enfermedad_base}
                      onChange={(e) => setFormData({ ...formData, enfermedad_base: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-300 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] transition-all bg-white appearance-none cursor-pointer"
                    >
                      <option value="">— Ninguna —</option>
                      <option value="Diabetes">Diabetes</option>
                      <option value="Hipertensión">Hipertensión</option>
                      <option value="Problemas circulatorios">Problemas circulatorios</option>
                      <option value="Artritis">Artritis</option>
                      <option value="Neuropatía">Neuropatía</option>
                      <option value="Obesidad">Obesidad</option>
                      <option value="Alergias">Alergias</option>
                      <option value="Otra">Otra...</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Contacto de Emergencia</label>
                  <input
                    type="text"
                    value={formData.contacto_emergencia}
                    onChange={(e) => setFormData({ ...formData, contacto_emergencia: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] transition-all"
                    placeholder="Nombre y teléfono de familiar"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Dirección</label>
                  <input
                    type="text"
                    value={formData.direccion}
                    onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Fecha de Nacimiento</label>
                  <input
                    type="date"
                    value={formData.fecha_nacimiento}
                    onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] transition-all"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Caso Clínico</label>
                  <textarea
                    value={formData.caso_clinico}
                    onChange={(e) => setFormData({ ...formData, caso_clinico: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 shadow-sm focus:ring-2 focus:ring-[#2A9D8F]/20 focus:border-[#2A9D8F] transition-all resize-none"
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row sm:justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  disabled={isSubmitting}
                  className="px-5 py-2.5 rounded-full font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={
                    isSubmitting || 
                    (country === 'CL' && (verificandoExistencia || pacienteExistente))
                  }
                  className="px-5 py-2.5 rounded-full font-medium bg-[#2A9D8F] text-white hover:bg-[#227f74] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {(isSubmitting || verificandoExistencia) && (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  )}
                  {verificandoExistencia ? 'Verificando...' : isSubmitting ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ─────────────────────────────────────────────────────────────────
            TABLA DE PACIENTES
        ───────────────────────────────────────────────────────────────── */}
        {filteredPacientes.length === 0 ? (
          <EmptyState isSearch={searchTerm.length > 0} />
        ) : (
          <>
            {/* Vista Desktop/Tablet */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F4E1D2]/30 h-12">
                    <th className="px-6 py-3 text-left text-xs font-bold text-[#0D2B28] uppercase tracking-wider">
                      Paciente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-[#0D2B28] uppercase tracking-wider">
                      Teléfono
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-[#0D2B28] uppercase tracking-wider hidden lg:table-cell">
                      Correo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-[#0D2B28] uppercase tracking-wider hidden xl:table-cell">
                      F. Nacimiento
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-bold text-[#0D2B28] uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPacientes.map((paciente) => (
                    <tr 
                      key={paciente.id} 
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                      onClick={() => navigate(`/app/fichas-clinicas?paciente_id=${paciente.id}`)}
                    >
                      {/* Columna Paciente (Avatar + Nombre + RUT) */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <PatientAvatar nombre={paciente.nombre} />
                          <div>
                            <p className="font-semibold text-gray-900">{paciente.nombre}</p>
                            <p className="text-sm text-gray-500">
                              {paciente.documento_id || paciente.rut || '—'}
                              {paciente.pais && paciente.pais !== 'CL' && ` (${paciente.pais})`}
                            </p>
                          </div>
                        </div>
                      </td>
                      
                      {/* Columna Teléfono */}
                      <td className="px-6 py-4">
                        <div className="flex items-center text-gray-700">
                          <PhoneIcon />
                          <span>{paciente.telefono}</span>
                        </div>
                      </td>
                      
                      {/* Columna Correo (oculta en móvil) */}
                      <td className="px-6 py-4 hidden lg:table-cell">
                        <div className="flex items-center text-gray-700">
                          <MailIcon />
                          <span className="truncate max-w-[200px]">{paciente.correo}</span>
                        </div>
                      </td>
                      
                      {/* Columna Fecha Nacimiento (oculta en móvil y tablet) */}
                      <td className="px-6 py-4 hidden xl:table-cell text-gray-600">
                        {paciente.fecha_nacimiento || '—'}
                      </td>
                      
                      {/* Columna Acciones */}
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditClick(paciente); }}
                            disabled={isDeleting === paciente.id}
                            className="p-2 rounded-full text-gray-400 hover:text-[#2A9D8F] hover:bg-[#2A9D8F]/10 transition-all duration-200 disabled:opacity-50"
                            title="Editar paciente"
                          >
                            <PencilIcon />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteClick(paciente.id); }}
                            disabled={isDeleting === paciente.id}
                            className="p-2 rounded-full text-gray-400 hover:text-[#E76F51] hover:bg-[#E76F51]/10 transition-all duration-200 disabled:opacity-50"
                            title="Eliminar paciente"
                          >
                            {isDeleting === paciente.id ? (
                              <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#E76F51] border-t-transparent"></div>
                            ) : (
                              <TrashIcon />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Vista Mobile (Cards) */}
            <div className="md:hidden divide-y divide-gray-100">
              {filteredPacientes.map((paciente) => (
                <div key={paciente.id} className="p-4 hover:bg-gray-50 transition-colors duration-150 cursor-pointer" onClick={() => navigate(`/app/fichas-clinicas?paciente_id=${paciente.id}`)}>
                  <div className="flex items-start justify-between gap-3">
                    {/* Info del Paciente */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <PatientAvatar nombre={paciente.nombre} />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-900 truncate">{paciente.nombre}</p>
                        <p className="text-sm text-gray-500">
                          {paciente.documento_id || paciente.rut || '—'}
                          {paciente.pais && paciente.pais !== 'CL' && ` (${paciente.pais})`}
                        </p>
                        <div className="flex items-center text-sm text-gray-600 mt-1">
                          <PhoneIcon />
                          <span>{paciente.telefono}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Botones de Acción */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEditClick(paciente); }}
                        disabled={isDeleting === paciente.id}
                        className="p-2.5 rounded-full text-gray-400 hover:text-[#2A9D8F] hover:bg-[#2A9D8F]/10 transition-all duration-200 active:scale-95"
                      >
                        <PencilIcon />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(paciente.id); }}
                        disabled={isDeleting === paciente.id}
                        className="p-2.5 rounded-full text-gray-400 hover:text-[#E76F51] hover:bg-[#E76F51]/10 transition-all duration-200 active:scale-95"
                      >
                        {isDeleting === paciente.id ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#E76F51] border-t-transparent"></div>
                        ) : (
                          <TrashIcon />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          MODAL DE EDICIÓN
      ═══════════════════════════════════════════════════════════════════ */}
      <EditPatientModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        paciente={selectedPaciente}
        onUpdate={handleUpdateFromModal}
        rut={rut}
        rutError={rutError}
        handleRutChange={handleRutChange}
        verificandoExistencia={verificandoExistencia}
        pacienteExistente={pacienteExistente}
        rutOriginal={rutOriginal}
        documentoId={documentoId}
        country={country}
        handleDocumentoIdChange={handleDocumentoIdChange}
        documentoIdError={documentoIdError}
        isUpdating={isUpdating}
      />
    </div>
  );
};

export default PacientesPage;
